/**
 * HistoryCards
 * Settlement explanation, parlay comparison, and daily briefing cards.
 */

import type {
  ComparisonParlay,
  DailyBriefingCardData,
  ParlayComparisonCardData,
  SettlementCardData,
  SettlementLeg,
} from '@parlay-picker/shared/chatTypes';
import { leagueKeyOrNull } from '@parlay-picker/shared/league';
import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import { getTeamAbbr, getTeamLogo } from '@parlay-picker/shared/teams';
import { usePlayerHeadshotUrl } from '@views/picks/utils';
import {
  Activity,
  BarChart3,
  Calendar,
  Clock,
  GitCompareArrows,
  Scale,
  TrendingUp,
} from 'lucide-react';
import type { ReactElement } from 'react';
import { ExpandableText, ResultBadge } from './cardHelpers';
import { GenericCard } from './InfoCards';

// =============================================================================
// Settlement Leg Row (extracted for hooks)
// =============================================================================

function SettlementLegRow({ leg }: { leg: SettlementLeg }): ReactElement {
  const hit = leg.hit;
  const margin = leg.margin != null ? Number(leg.margin) : null;
  const playerName = String(leg.player || '');
  const team = leg.team ? String(leg.team) : null;
  const opponentTeam = leg.opponentTeam ? String(leg.opponentTeam) : null;
  const position = leg.playerPosition ? String(leg.playerPosition) : null;

  const legLeagueKey = leagueKeyOrNull(leg.leagueKey);
  const headshotUrl = usePlayerHeadshotUrl(playerName || null, legLeagueKey);
  const teamAbbr = team ? getTeamAbbr(team, legLeagueKey) : null;
  const oppAbbr = opponentTeam ? getTeamAbbr(opponentTeam, legLeagueKey) : null;
  const teamLogoUrl = team ? getTeamLogo(team, legLeagueKey) : null;

  return (
    <div
      className={`rounded-lg border px-3 py-2 ${
        hit === true
          ? 'border-theme-success/30 bg-theme-success/5'
          : hit === false
            ? 'border-theme-danger/30 bg-theme-danger/5'
            : 'border-theme-border bg-theme-surface'
      }`}
    >
      <div className="flex items-start gap-2.5">
        {/* Player headshot or team logo fallback */}
        <div className="w-8 h-8 rounded-full overflow-hidden bg-theme-surface-hover shrink-0 mt-0.5">
          {headshotUrl ? (
            <img
              src={headshotUrl}
              alt={playerName}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
          ) : teamLogoUrl ? (
            <img
              src={teamLogoUrl}
              alt={teamAbbr ?? ''}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-contain p-0.5"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-theme-text-tertiary">
              {playerName.charAt(0) || '?'}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-sm font-medium text-theme-primary truncate">
                {playerName || '?'}
              </span>
              {position && (
                <span className="text-[10px] font-medium text-theme-text-tertiary bg-theme-surface-hover px-1 py-0.5 rounded shrink-0">
                  {position}
                </span>
              )}
              {teamAbbr && (
                <span className="text-[10px] text-theme-text-tertiary shrink-0">
                  {teamAbbr}
                  {oppAbbr ? ` vs ${oppAbbr}` : ''}
                </span>
              )}
            </div>
            <span
              className={`text-xs font-bold shrink-0 ml-2 ${hit === true ? 'text-theme-success' : hit === false ? 'text-theme-danger' : 'text-theme-secondary'}`}
            >
              {hit === true ? 'HIT' : hit === false ? 'MISS' : 'VOID'}
            </span>
          </div>

          <div className="text-xs text-theme-text-secondary mt-0.5">
            {String(leg.overUnder || '')} {String(leg.line ?? '')}{' '}
            {formatStatLabel(String(leg.statType || ''))}
          </div>

          <div className="flex items-center gap-3 mt-1 text-xs text-theme-secondary">
            {leg.actualValue != null && (
              <span>
                Actual: <strong className="text-theme-primary">{String(leg.actualValue)}</strong>
              </span>
            )}
            {margin != null && (
              <span className={hit === true ? 'text-theme-success' : 'text-theme-danger'}>
                {margin > 0 ? '+' : ''}
                {margin.toFixed(1)} margin
              </span>
            )}
            {leg.predictedValue != null && <span>AI predicted: {String(leg.predictedValue)}</span>}
          </div>
          {leg.aiReasoning ? (
            <p className="mt-1.5 text-xs text-theme-secondary line-clamp-2">
              {String(leg.aiReasoning)}
            </p>
          ) : null}
          {leg.aiConfidence ? (
            <div className="flex gap-2 mt-1 text-xs">
              <span className="text-theme-secondary">
                Confidence: <strong>{String(leg.aiConfidence)}</strong>
              </span>
              {leg.aiEdge ? (
                <span className="text-theme-secondary">
                  Edge: <strong>{String(leg.aiEdge)}</strong>
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Settlement Explanation Card
// =============================================================================

export function SettlementExplanationCard({ data }: { data: SettlementCardData }): ReactElement {
  const result = data.result || 'unknown';
  const totalOdds = data.totalOdds || null;
  const hasAiData = data.hasAiData === true;
  const legs = data.legs || [];

  return (
    <div className="rounded-xl border border-theme-border bg-theme-surface p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Scale className="h-4 w-4 text-theme-primary" />
        <span className="text-sm font-semibold text-theme-primary">Settlement Explanation</span>
        <ResultBadge result={result} />
        {totalOdds && <span className="ml-auto text-xs text-theme-secondary">{totalOdds}</span>}
      </div>

      {!hasAiData && (
        <div className="text-xs text-theme-warning bg-theme-warning/10 rounded-lg px-3 py-2">
          Manually tracked — no AI generation data available
        </div>
      )}

      {data.parlayReasoning ? (
        <ExpandableText
          text={String(data.parlayReasoning)}
          className="text-xs text-theme-text-secondary italic"
          lineClamp={3}
        />
      ) : null}

      <div className="space-y-2">
        {legs.map((leg, i) => (
          <SettlementLegRow key={i} leg={leg} />
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// Parlay Comparison Card
// =============================================================================

export function ParlayComparisonCard({ data }: { data: ParlayComparisonCardData }): ReactElement {
  const parlay1 = data.parlay1;
  const parlay2 = data.parlay2;
  const overlapping = data.overlappingPlayers || [];

  if (!parlay1 || !parlay2) {
    return (
      <GenericCard data={data as unknown as Record<string, unknown>} type="parlay_comparison" />
    );
  }

  const renderParlayCol = (p: ComparisonParlay, label: string): ReactElement => {
    const legs = p.legs || [];
    const result = p.result || 'pending';
    return (
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-semibold text-theme-primary">{label}</span>
          <ResultBadge result={result} />
        </div>
        <div className="text-xs text-theme-secondary mb-2">
          {String(p.totalOdds || '?')} odds • {legs.length} legs
        </div>
        <div className="space-y-1">
          {legs.map((l, i) => (
            <div key={i} className="flex items-center gap-1 text-xs min-w-0">
              <span
                className={`shrink-0 ${
                  l.hit === true
                    ? 'text-theme-success'
                    : l.hit === false
                      ? 'text-theme-danger'
                      : 'text-theme-secondary'
                }`}
              >
                {l.hit === true ? '\u2713' : l.hit === false ? '\u2717' : '\u25CB'}
              </span>
              <span className="text-theme-primary truncate">
                {String(l.player || '?')} {String(l.overUnder || '')} {String(l.line ?? '')}{' '}
                {formatStatLabel(String(l.statType || ''))}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-theme-border bg-theme-surface p-4 space-y-3">
      <div className="flex items-center gap-2">
        <GitCompareArrows className="h-4 w-4 text-theme-primary" />
        <span className="text-sm font-semibold text-theme-primary">Parlay Comparison</span>
      </div>
      <div className="flex gap-3">
        {renderParlayCol(parlay1, `#${parlay1.parlayId}`)}
        <div className="w-px bg-theme-border shrink-0" />
        {renderParlayCol(parlay2, `#${parlay2.parlayId}`)}
      </div>
      {overlapping.length > 0 && (
        <div className="text-xs text-theme-warning bg-theme-warning/10 rounded-lg px-3 py-1.5">
          Overlapping players: {overlapping.join(', ')}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Daily Briefing Card
// =============================================================================

export function DailyBriefingCard({ data }: { data: DailyBriefingCardData }): ReactElement {
  const games = data.games || [];
  const pendingCount = data.pendingCount || 0;
  const recentResults = data.recentResults || [];
  const winRateTrend = data.winRateTrend;

  return (
    <div className="rounded-xl border border-theme-border bg-theme-surface p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-theme-primary" />
        <span className="text-sm font-semibold text-theme-primary">Daily Briefing</span>
      </div>

      {/* Games section */}
      {games.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <Activity className="h-3.5 w-3.5 text-theme-secondary" />
            <span className="text-xs font-medium text-theme-secondary">
              Tonight&apos;s Games ({games.length})
            </span>
          </div>
          <div className="grid grid-cols-2 gap-1">
            {games.slice(0, 6).map((g, i) => (
              <div key={i} className="text-xs text-theme-primary flex items-center gap-1">
                <span className="truncate">
                  {String(g.away)} @ {String(g.home)}
                </span>
                <span className="text-theme-secondary shrink-0">{String(g.startTime)}</span>
              </div>
            ))}
          </div>
          {games.length > 6 && (
            <span className="text-xs text-theme-secondary">+{games.length - 6} more</span>
          )}
        </div>
      )}

      {/* Pending parlays */}
      {pendingCount > 0 && (
        <div className="flex items-center gap-1.5 text-xs">
          <Clock className="h-3.5 w-3.5 text-theme-warning" />
          <span className="text-theme-primary">
            {pendingCount} active parlay{pendingCount !== 1 ? 's' : ''} in play
          </span>
        </div>
      )}

      {/* Recent results */}
      {recentResults.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <BarChart3 className="h-3.5 w-3.5 text-theme-secondary" />
            <span className="text-xs font-medium text-theme-secondary">Recent Results</span>
          </div>
          <div className="flex gap-1.5">
            {recentResults.map((r, i) => (
              <div
                key={i}
                className={`text-xs px-2 py-1 rounded-md ${String(r.result) === 'won' ? 'bg-theme-success/10 text-theme-success' : 'bg-theme-danger/10 text-theme-danger'}`}
              >
                #{String(r.parlayId)} {String(r.result)} ({String(r.legCount)}L)
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Win rate trend */}
      {winRateTrend && (
        <div className="flex items-center gap-3 pt-1 border-t border-theme-border">
          <div className="flex items-center gap-1">
            <TrendingUp className="h-3.5 w-3.5 text-theme-success" />
            <span className="text-xs text-theme-primary">
              <strong>{String(winRateTrend.last7d)}%</strong> last 7d
            </span>
          </div>
          <div className="text-xs text-theme-secondary">
            {String(winRateTrend.overall)}% overall ({String(winRateTrend.overallSample)} parlays)
          </div>
        </div>
      )}
    </div>
  );
}
